import { Event as GlobalEvent } from "../../Global/Base/Events";
import { IDisposable } from "../../Global/Base/IDisposable";
/**
     *Rozhraní továrny, která vytváří UI reprezentaci kalendářového tooltipu.
     */
export interface ICalendarTooltipCreator {
    /** Vytvoří UI reprezentaci tooltipu pro položku kalendáře.
     * @param tooltip Komponenta tooltipu, pro kterou se má vytvořit UI reprezentace.
     */
    createCalendarTooltip(tooltip: ICalendarTooltip): Promise<HTMLElement>;
}

export class ICalendarTooltipCreator {
    private static _methods = ["createCalendarTooltip"];

    public static [Symbol.hasInstance](instance: any): boolean {
        return instance != null && !this._methods.some(value => typeof instance[value] !== "function");
    }
}

/**
* Rozhraní komponenty kalendářového tooltipu.
*/
export interface ICalendarTooltip extends IDisposable {
    /**
    * Data události
    */
    calendarItemData: ICalendarItemData;

    /**
     * Flag, jestli je tooltip blokován pro zavření.
     */
    isBlocked: boolean;

    /**
     * Flag, jestli se kurzor myši nachází nad zobrazeným tooltipem.
     */
    isMouseOver: boolean;

    /**
    * Zobrazí tooltip
    * @param startElement Html element, na kterém se tooltip zobrazí.
    */
    show(startElement: HTMLElement, event: Event): Promise<void>;

    /**
    * Zavře tooltip
    */
    close(): void;

    /**
    * Zablokuje tooltip pro zavření.
    */
    block(): void;

    /**
    * Uvolní tooltip, aby mohl být zavřen.
    * @param force Zavře tooltip násilím.
    */
    release(force?: boolean): void;

    /**
    * Událost, která nastane když je tooltip zobrazen.
    */
    readonly opened: GlobalEvent<ICalendarTooltip, void>;

    /**
    * Událost, která nastane když je tooltip zavřen.
    */
    readonly closed: GlobalEvent<ICalendarTooltip, void>;
}

export class ICalendarTooltip {
    private static _methods = ["show", "close", "block", "release"];
    private static _properties = ["opened", "closed", "calendarItemData", "isBlocked", "isMouseOver"];

    public static [Symbol.hasInstance](instance: any): boolean {
        return instance != null && !this._properties.some((value) => !Reflect.has(instance, value)) &&
            !this._methods.some(value => typeof instance[value] !== "function") && instance instanceof IDisposable;
    }
}

/**
 * Data události v kalendáři
 */
export interface ICalendarItemData {
    /**
     * Klíč záznamu
     */
    dataKey: string;

    /**
     * Nadpis události
     */
    caption: string;

    /**
     * Datum od
     */
    dateFrom: Date;

    /**
     * Datum do
     */
    dateTo: Date;

    /**
     * Barva události
     */
    color: string;

    /**
     * Příznak, zda zobrazit čas konání
     */
    includeTime: boolean;

    /**
     * Odkaz na formulář, který se má použít pro detail nebo editaci.
     */
    showFormID: string;

    /**
     * Seznam atributů, které kalendářová položka zobrazuje.
     */
    textFields: string[];

    /**
     * Pole hodnot pro atributy definované v textFields.
     */
    textFieldsValues: string[];
}

export class ICalendarItemData {
    private static _properties = ["dataKey", "caption", "dateFrom", "dateTo", "color", "includeTime", "showFormID", "textFields", "textFieldsValues"];

    public static [Symbol.hasInstance](instance: any): boolean {
        return instance != null && !this._properties.some((value) => !Reflect.has(instance, value));
    }
}
